import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import useIntl from 'useIntl';
import useOutsideClick from 'useOutsideClick';
import AppUtils from '@control-front-end/utils/utils';
import { GET_TEMPLATE_ACTORS } from '@control-front-end/common/constants/graphActors';
import { Label, Icon, Avatar, ProgressBar } from 'mw-style-react';
import { SEARCH, RECENT } from 'constants';
import mes from './intl';
import './SmartChipPopup.scss';

const DEFAULT_SEARCH_FILTERS = 'actors,graphFolders';
const initState = {
  list: [],
  limit: 15,
  offset: 0,
  reqStatus: 'success',
  endList: false,
};

/**
 * Попап для добавления смарт чипа с превью
 * @returns {*}
 * @constructor
 */
function SmartChipPopup({ query, users: members = [], onSelect, onClose }) {
  const t = useIntl();
  const dispatch = useDispatch();
  const workspaceUsers = useSelector((state) => state.users);
  const systemForms = useSelector((state) => state.systemForms);
  const [filters, setFilters] = useState(DEFAULT_SEARCH_FILTERS);
  const [suggestions, setSuggestions] = useState({});
  const [search, setSearch] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const eventsFormId = systemForms.events.id;
  const isDefaultFilter = filters === DEFAULT_SEARCH_FILTERS;
  const noSuggestions = Object.keys(suggestions).every(
    (key) => !suggestions[key].length
  );
  const usersIds = members.map((u) => u.userId);
  const users = workspaceUsers.filter((i) => usersIds.includes(i.id));

  const smartPopupRef = useRef();
  useOutsideClick({
    ref: smartPopupRef,
    callback: (e) => {
      const chipSearchFieldEl = document.querySelector('div.searchChip');
      if (chipSearchFieldEl && chipSearchFieldEl.contains(e.target)) return;
      onClose();
    },
  });

  const setSuggestionsList = (list, objType) => {
    const typeSuggestions = list.map((i) => ({ ...i, objType }));
    setSuggestions((prevSuggestions) => ({
      ...prevSuggestions,
      [objType]: typeSuggestions,
    }));
  };

  const getRecentGraphAndActors = () => {
    dispatch({
      type: RECENT.REQUEST,
      payload: { objTypes: 'actor,graphFolder', limit: 50 },
      callback: (data) => {
        const noEventsList = data.filter((i) => i.formId !== eventsFormId);
        const recentGrouped = AppUtils.groupBy(noEventsList, 'objType');
        Object.keys(recentGrouped).forEach((key) =>
          setSuggestionsList(recentGrouped[key], key)
        );
      },
    });
  };

  const getRecentEvents = () => {
    dispatch({
      type: GET_TEMPLATE_ACTORS.REQUEST,
      payload: {
        formId: eventsFormId,
        orderBy: 'updated_at',
        loadMore: false,
        localState: initState,
      },
      callback: ({ list }) => {
        const events = list.map((i) => ({ ...i, objType: 'event' }));
        setSuggestionsList(events, 'event');
      },
    });
  };

  const getFilteredUsers = (searchString) => {
    const filteredUsers = users.filter((i) => {
      const nick = i.value.toLowerCase();
      const nickT = AppUtils.transliterate(nick);
      return (
        nick.indexOf(searchString) !== -1 ||
        nickT.indexOf(searchString) !== -1 ||
        i.email.toLowerCase().indexOf(searchString) !== -1
      );
    });
    return filteredUsers.map((i) => ({ ...i, objType: 'user' }));
  };

  useEffect(() => {
    getRecentGraphAndActors();
    getRecentEvents();
  }, []);

  useEffect(() => {
    if (query.length < 3) return;
    setLoading(true);
    dispatch({
      type: SEARCH.REQUEST,
      payload: { filters, query },
      callback: (data) => {
        const filteredUsers = getFilteredUsers(query);
        const result = data.map((i) => ({
          ...i,
          objType: i.formId === eventsFormId ? 'event' : i.objType,
        }));
        const fullResult = [...filteredUsers, ...result];
        setSearch(fullResult);
        setLoading(false);
      },
    });
  }, [query, filters]);

  const getIconType = (objType) => {
    switch (objType) {
      case 'event':
        return 'event';
      case 'graphFolder':
        return 'graph';
      case 'actor':
        return 'actor';
      default:
        return '';
    }
  };

  const renderList = (list) =>
    list.map((item) => (
      <div
        key={`${item.id}_${item.objType}`}
        styleName="smartChip__results__item"
        onClick={() => {
          onSelect({
            id: item.id,
            objType: item.objType || item.type,
            title: item.title || item.name || item.nick || item.value,
          });
        }}
      >
        <Avatar
          styleName="smartChip__results__item__avatar"
          size="small"
          src={item.avatar || item.pictureUrl}
        >
          <Icon
            type={getIconType(item.objType || item.type)}
            visibility={item.avatar || item.pictureUrl ? 'hidden' : 'visible'}
          />
        </Avatar>
        <Label
          fontSize="small"
          value={item.title || item.name || item.nick || item.value}
        />
      </div>
    ));

  const getIsVisible = (objType) =>
    isDefaultFilter || filters.indexOf(objType) !== -1;

  const renderSuggestions = () => {
    if (noSuggestions) {
      return (
        <div styleName="smartChip__results__empty">
          <Label value={t(mes.noSuggestions)} />
        </div>
      );
    }
    const {
      actor: actors,
      graphFolder: graphFolders,
      event: events,
    } = suggestions;
    const getCountToShow = (list) => (isDefaultFilter ? 2 : list.length);
    return (
      <>
        {users.length && getIsVisible('user') ? (
          <div styleName="smartChip__results__group">
            <Icon
              styleName="smartChip__results__group__icon"
              type="arrow"
              onClick={() => setFilters('users')}
              visibility={isDefaultFilter ? 'visible' : 'hidden'}
            />
            <Label
              fontSize="small"
              fontWeight="semibold"
              value={t(mes.usersChipResult)}
            />
            {renderList(users.slice(0, getCountToShow(users)))}
          </div>
        ) : null}
        {events && events.length && getIsVisible('event') ? (
          <div styleName="smartChip__results__group">
            <Icon
              styleName="smartChip__results__group__icon"
              type="arrow"
              onClick={() => setFilters('events')}
              visibility={isDefaultFilter ? 'visible' : 'hidden'}
            />
            <Label
              fontSize="small"
              fontWeight="semibold"
              value={t(mes.eventChipResult)}
            />
            {renderList(events.slice(0, getCountToShow(events)))}
          </div>
        ) : null}
        {actors && actors.length && getIsVisible('actor') ? (
          <div styleName="smartChip__results__group">
            <Icon
              styleName="smartChip__results__group__icon"
              type="arrow"
              onClick={() => setFilters('actors')}
              visibility={isDefaultFilter ? 'visible' : 'hidden'}
            />
            <Label
              fontSize="small"
              fontWeight="semibold"
              value={t(mes.actorChipResult)}
            />
            {renderList(
              suggestions.actor.slice(0, getCountToShow(suggestions.actor))
            )}
          </div>
        ) : null}
        {graphFolders && graphFolders.length && getIsVisible('graphFolder') ? (
          <div styleName="smartChip__results__group">
            <Icon
              styleName="smartChip__results__group__icon"
              type="arrow"
              onClick={() => setFilters('graphFolders')}
              visibility={isDefaultFilter ? 'visible' : 'hidden'}
            />
            <Label
              fontSize="small"
              fontWeight="semibold"
              value={t(mes.graphFolderChipResult)}
            />
            {renderList(
              suggestions.graphFolder.slice(
                0,
                getCountToShow(suggestions.graphFolder)
              )
            )}
          </div>
        ) : null}
      </>
    );
  };

  const renderSearchResults = () => {
    if (isLoading) {
      return (
        <div styleName="smartChip__results__loader">
          <ProgressBar type="circle" size="small" />
        </div>
      );
    }
    if (!search.length) {
      return (
        <div styleName="smartChip__results__empty">
          <Label value={t(mes.noResultsFound)} />
        </div>
      );
    }
    let groupedResults;
    if (isDefaultFilter) {
      groupedResults = AppUtils.groupBy(search, 'objType');
    } else {
      groupedResults = { [filters]: search };
    }
    return Object.keys(groupedResults).map((key) => {
      const groupedList = groupedResults[key];
      return (
        <div key={key} styleName="smartChip__results__group">
          <Icon
            styleName="smartChip__results__group__icon"
            type="arrow"
            onClick={() =>
              setFilters(key === 'graph' ? 'graphFolders' : `${key}s`)
            }
            visibility={isDefaultFilter ? 'visible' : 'hidden'}
          />
          <Label
            fontSize="small"
            fontWeight="semibold"
            value={t(mes[`${key}ChipResult`])}
          />
          {renderList(groupedList)}
        </div>
      );
    });
  };

  return (
    <div ref={smartPopupRef} styleName="smartChip">
      <div styleName="smartChip__results">
        {query.length > 2 ? renderSearchResults() : renderSuggestions()}
      </div>
    </div>
  );
}

SmartChipPopup.propTypes = {
  query: PropTypes.string,
  users: PropTypes.array,
  onSelect: PropTypes.func,
  onClose: PropTypes.func,
};

export default SmartChipPopup;
